import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MessageCircle, PackageX } from "lucide-react";
import { API, errMsg } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;
const webp = (u, w) => `${BASE}${u}&fmt=webp&w=${w}`;

function Photo({ u, alt, first }) {
  return <img src={webp(u, 400)} srcSet={`${webp(u, 200)} 200w, ${webp(u, 400)} 400w`} sizes="(max-width: 640px) 50vw, 25vw" width="400" height="400"
    alt={alt} loading={first ? "eager" : "lazy"} fetchPriority={first ? "high" : "auto"} decoding="async" className="w-full h-full object-cover" />;
}

function Card({ it, phone, i }) {
  const [idx, setIdx] = useState(0);
  const msg = encodeURIComponent(`Hi, I'm interested in ${it.name}.`);
  return (
    <div data-testid={`catalog-item-${i}`} className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 overflow-hidden flex flex-col min-w-0">
      <div className="aspect-square bg-slate-100">
        {it.photos.length ? <Photo u={it.photos[idx].thumb} alt={it.name} first={i === 0} /> : <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">No photo</div>}
      </div>
      {it.photos.length > 1 && <div className="flex flex-wrap gap-1.5 px-2.5 sm:px-3 pt-2">{it.photos.map((p, j) => <button key={j} onClick={() => setIdx(j)} aria-label={`Photo ${j + 1}`} className={`w-9 h-9 rounded overflow-hidden border ${j === idx ? "border-[#262357]" : "border-transparent"}`}><img src={webp(p.thumb, 200)} width="36" height="36" loading="lazy" alt="" className="w-full h-full object-cover" /></button>)}</div>}
      <div className="p-2.5 sm:p-4 flex-1 flex flex-col min-w-0">
        {it.category && <p className="text-[10px] uppercase tracking-wider text-teal-700 font-bold">{it.category}</p>}
        <p className="font-semibold text-sm sm:text-base text-slate-900 mt-0.5 break-words">{it.name}</p>
        {it.description && <p className="text-xs text-slate-600 mt-1 line-clamp-3">{it.description}</p>}
        <div className="flex flex-wrap items-baseline gap-x-2 mt-3">
          {it.rate !== null && <span data-testid={`catalog-rate-${i}`} className="font-display text-base sm:text-lg font-bold text-[#262357]">₹{it.rate}<span className="text-xs font-normal text-slate-600">/{it.unit}</span></span>}
          {it.mrp && <span className="text-xs text-slate-500 line-through">MRP ₹{it.mrp}</span>}
        </div>
        {it.stock && <p data-testid={`catalog-stock-${i}`} className={`text-xs font-semibold mt-1 ${it.stock === "In stock" ? "text-emerald-700" : it.stock === "Low stock" ? "text-amber-600" : "text-red-600"}`}>{it.stock}</p>}
        {phone && <a data-testid={`catalog-enquire-${i}`} href={`https://wa.me/91${phone}?text=${msg}`} target="_blank" rel="noreferrer" className="mt-auto pt-0 h-11 sm:h-9 mt-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-700 text-white text-xs sm:text-sm font-semibold hover:bg-emerald-800"><MessageCircle className="w-4 h-4 shrink-0" /> <span className="sm:hidden">Enquire</span><span className="hidden sm:inline">Enquire on WhatsApp</span></a>}
      </div>
    </div>
  );
}

export default function PublicCatalog() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { axios.get(`${API}/public/catalog/${token}`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e))); }, [token]);
  if (error) return <div data-testid="catalog-error" className="min-h-screen flex flex-col items-center justify-center gap-3 text-slate-600"><PackageX className="w-10 h-10 text-slate-300" />{error}</div>;
  if (!data) return null;
  return (
    <div className="min-h-screen bg-[#f6f8fb]" data-testid="public-catalog">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-5 py-4 sm:py-5 flex items-center gap-4">
          {data.business.logo && <img src={`${BASE}${data.business.logo}`} alt="" height="48" className="h-12 w-auto" />}
          <div><p data-testid="catalog-business" className="font-display text-xl font-bold text-[#262357]">{data.business.name}</p><p className="text-sm text-slate-600">{data.title || (data.items.length === 1 ? "Product" : "Product catalog")}</p></div>
        </div>
      </header>
      <main data-testid="catalog-grid" className={`max-w-6xl mx-auto px-3 sm:px-5 py-5 sm:py-8 grid gap-3 sm:gap-5 ${data.items.length === 1 ? "max-w-md" : "grid-cols-2 lg:grid-cols-4"}`}>
        {data.items.map((it, i) => <Card key={it.id} it={it} i={i} phone={data.business.phone} />)}
      </main>
      <p className="text-center text-xs text-slate-600 pb-8">Generated with BusinessBook</p>
    </div>
  );
}
